import { Grid, Theme, Typography, withStyles, Button } from '@material-ui/core'
import { toLower } from 'lodash'
import ChevronRightIcon from 'mdi-react/ChevronRightIcon'
import { useEffect, useState } from 'react'
import LoadingContainer from '../../../components/LoadingContainer/LoadingContainer'
import tickIcon from '../../../images/tick.svg'
import apartmentAPI from '../../../services/apartments'
import Api from '../../../services/applications'
import i18n from '../../../translations/i18n'
import { MuiClasses } from '../../../types'

// import NextStepButton from '../NextStepButton'
import Suggest from '../Steps/Suggest'

const styles = ({ breakpoints, typography }: Theme) => ({
  customSuggestTextStyles: {
    fontSize: typography.pxToRem(38),
    fontWeight: typography.fontWeightLight,
    fontFamily: 'CircularRegular',
    maxWidth: '97%',
    margin: '1rem auto',
    lineHeight: '1.2',
    [breakpoints.down('sm')]: {
      maxWidth: '100%',
      lineHeight: '1.1',
      fontSize: typography.pxToRem(28)
    }
  },
  blackText: {
    color: '#000000'
  },
  infoText: {
    fontSize: typography.pxToRem(18)
  },
  guaranteeText: {
    marginTop: '37px',
    marginBottom: '39px',
    [breakpoints.down('sm')]: {
      marginTop: '45px',
      marginBottom: '56px',
      fontSize: '1.6rem'
    }
  },
  infoContainer: {
    display: 'flex',
    fontWeight: typography.fontWeightLight,
    marginBottom: '41px'
  },
  tickIcon: {
    marginRight: '13px',
    marginTop: '10px'
  },
  primaryBtn: {
    height: '82px',
    width: '100%',
    textTransform: 'capitalize' as const,
    fontWeight: typography.fontWeightBold,
    fontSize: '24px',
    marginTop: '60px',
    '@media (min-width: 780px)': {
      height: '65px'
    }
  },
  buttonContainer: {
    minWidth: '100%',
    justifyContent: 'center' as const,
    margin: '50px 0 72px 0 !important',
    '@media (min-width: 780px)': {
      margin: '50px 0 50px 0'
    }
  },
  fullWidth: {
    width: '100%'
  },
  horizontalPadding4: {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem'
  },
  verticalPadding: {
    paddingTop: '39px',
    '@media (min-width: 780px)': {
      paddingTop: '70px'
    }
  }
})

type InstantRentPromptProps = {
  classes: MuiClasses
  beforeLogin?: boolean
  apartmentCode: string
  t: i18n.TFunction
  navigateTo: (arg: number) => void
}

const InstantRentPrompt = ({
  classes,
  beforeLogin,
  apartmentCode,
  t,
  navigateTo
}: InstantRentPromptProps) => {
  const [isApartment, setApartment] = useState(null) as any
  const [isLoading, setLoading] = useState(false)

  const getApartmentByCodeHandler = () => {
    setLoading(true)
    apartmentAPI
      .getApartmentByCode(apartmentCode)
      .then((apartment) => {
        setApartment(apartment)
        localStorage.setItem('apartment', JSON.stringify(apartment))
        setLoading(false)

        if (apartment?.status === 'rented') {
          showRentedAlertAndRedirect()
        }

        //handle locked apartment for other users
        // if (
        //   apartment.status === 'rentContractWanted' &&
        //   apartment.applicationIds?.contractWantedId &&
        //   !localStorage.getItem('application')
        // ) {
        //   /*TODO:
        //    - convert statusUpdatedAt timestamp to dateTime
        //    - find the difference of the values to current time in minutes
        //    - set the difference in minutes to setApartmentLocked()
        //   */
        //   setApartmentLocked(5)
        // }
      })
      .catch((error) => {
        console.log(error.response)
        showRentedAlertAndRedirect()
        setLoading(false)
      })
  }

  const showRentedAlertAndRedirect = () => {
    alert(t('instantRent.apartmentRented'))
    setTimeout(() => {
      window.location.href = 'https://fiksuvuokraus.fi/'
    }, 0)
  }

  const handleFormEvent = () => {
    if (localStorage.getItem('application')) {
      navigateTo(2)
      return
    }

    setLoading(true)
    Api.startInstantRent({
      apartmentCode,
      language: localStorage.getItem('language') || 'en'
    })
      .then((application) => {
        setLoading(false)
        localStorage.setItem('application', JSON.stringify(application))
        navigateTo(2)
      })
      .catch((error) => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getApartmentByCodeHandler()
    setApartment(JSON.parse(localStorage.getItem('apartment') || '{}'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {isLoading ? (
        <LoadingContainer />
      ) : (
        <Grid
          className={`${classes.horizontalPadding4} ${classes.verticalPadding}`}
        >
          <Suggest
            content={`${t('instantRent.locationInfo')} ${isApartment?.address}`}
            hideSuggestIcon
            isModal
            customSuggestTextStyles={`${classes.customSuggestTextStyles} ${classes.centeredText}`}
          />
          <div className={classes.fullWidth}>
            <Typography
              variant='h4'
              className={`${classes.blackText} ${classes.centeredText} ${classes.guaranteeText}`}
            >
              {t('instantRent.guaranteeText')}
            </Typography>
          </div>
          <Grid
            container
            className={classes.gridContainer}
            spacing={4}
            style={{ justifyContent: 'space-between' }}
          >
            <Grid item md={4} xs={12} className={classes.infoContainer}>
              <div>
                <img
                  src={tickIcon}
                  className={classes.tickIcon}
                  alt={'Tick Icon'}
                />
              </div>
              <Typography
                variant='body1'
                className={`${classes.blackText} ${classes.mdText}`}
                style={{
                  opacity: '0.6'
                }}
              >
                {t('instantRent.infoBox1')}
              </Typography>
            </Grid>
            <Grid item md={4} xs={12} className={classes.infoContainer}>
              <div>
                <img
                  src={tickIcon}
                  className={classes.tickIcon}
                  alt={'Tick Icon'}
                />
              </div>
              <Typography
                variant='body1'
                className={`${classes.blackText} ${classes.mdText}`}
                style={{
                  opacity: '0.6'
                }}
              >
                {`${t('instantRent.rentAmount')}${(
                  isApartment?.price || ''
                ).toLocaleString()} 
                ${t('instantRent.waterTitle')}
                ${
                  isApartment?.waterFee
                    ? '€' +
                      (isApartment?.waterFee || '').toLocaleString() +
                      t('abbreviations.perPersonPerMonth')
                    : toLower(isApartment?.waterFeeBasisOption)
                }${t('instantRent.infoBox2a')} €${(
                  isApartment?.securityDepositAmount || ''
                ).toLocaleString()},`}{' '}
                {t('instantRent.infoBox2b')}
              </Typography>
            </Grid>
            <Grid item md={4} xs={12} className={classes.infoContainer}>
              <div>
                <img
                  src={tickIcon}
                  className={classes.tickIcon}
                  alt={'Tick Icon'}
                />
              </div>
              <Typography
                variant='body1'
                className={`${classes.blackText} ${classes.mdText}`}
                style={{
                  opacity: '0.6'
                }}
              >
                {t('instantRent.infoBox3')}
                {'\n'}
                <a
                  href='https://fiksuvuokraus.fi/tietosuojaseloste/'
                  target='_blank'
                  rel='noreferrer'
                  className={classes.underlinedLink}
                >
                  {t('instantRent.privacyStatement')}{' '}
                </a>
                &gt;
              </Typography>
            </Grid>
          </Grid>
          <div className={classes.fullWidth}>
            <Grid container className={classes.buttonContainer}>
              <Grid item md={9} xs={12}>
                {/* <NextStepButton
                  type='button'
                  customLabel={t('instantRent.buttonLabels.startRenting')}
                  customStyles={classes.primaryBtn}
                  classes={classes}
                  beforeLogin={beforeLogin}
                /> */}
                <Button
                  fullWidth
                  variant='contained'
                  color='primary'
                  className={`${classes.responsiveButton}`}
                  onClick={handleFormEvent}
                >
                  <Grid container justify='center' alignItems='center'>
                    <Grid item>{t('general.next')}</Grid>
                    <Grid item className={classes.rightFloatingIcon}>
                      <ChevronRightIcon color='#D8DCDF' />
                    </Grid>
                  </Grid>
                </Button>
                {/* {isApartmentLocked && (
                  <Typography
                    align='center'
                    variant='body1'
                    className={`${classes.blackText} ${classes.mdText}`}
                    style={{
                      color: 'red'
                    }}
                  >
                    {t('instantRent.placeholders.aparmentBookedNotice', {
                      counter: isApartmentLocked
                    })}
                  </Typography>
                )} */}
              </Grid>
            </Grid>
          </div>
        </Grid>
      )}
    </>
  )
}

export default withStyles(styles)(InstantRentPrompt)
